// Pure helpers for composing the file-request notification email
// (see emailListingRequestRecipients in helper.js). NO Firebase imports — the
// field/format logic stays unit-testable in isolation.

// Listing Request form yes/no codes -> display labels. Anything that isn't a
// recognized answer (blank, undefined) -> "" so the caller skips the line.
export function yesNoLabel(value) {
  if (value === "yes") return "Yes";
  if (value === "no") return "No";
  return "";
}

// Seller's-disclosure codes -> human labels. Unknown/blank -> "".
const DISCLOSURE_LABELS = {
  seller_disclosure: "Sellers Disclosure",
  seller_disclosure_alternative: "Sellers Disclosure Alternative",
  seller_disclosure_alternative_as_is:
    "Sellers Disclosure Alternative with AS-IS",
};

export function disclosureLabel(value) {
  return DISCLOSURE_LABELS[value] || "";
}

// Build one client line "Name — phone — email" from a normalized contact
// (normalizeContact exposes .name, .phone, .email). Missing parts drop out; an
// empty/falsy contact -> "".
export function formatClientLine(contact) {
  if (!contact) return "";
  return [contact.name, contact.phone, contact.email]
    .map((part) => (part || "").trim())
    .filter(Boolean)
    .join(" — ");
}

// Compose the ordered [{ label, value }] rows for the email body from a request
// (deal) object plus its already-resolved, normalized client contacts.
// `formatDate` is injected (it lives in helper.js) to keep this module pure.
// Empty values are omitted, so blank optional fields simply don't appear.
export function buildListingRequestFields({
  deal = {},
  clients = [],
  agentName = "",
  formatDate = (v) => v,
} = {}) {
  const rows = [];
  const push = (label, value) => {
    const v = (value == null ? "" : String(value)).trim();
    if (v) rows.push({ label, value: v });
  };

  push("Agent", agentName);
  push(
    "Listing appointment",
    deal.listingDate ? formatDate(deal.listingDate) : "",
  );
  push("Need file by", deal.fileDeadline ? formatDate(deal.fileDeadline) : "");
  push("Property", deal.propertyaddress);

  // One numbered line per client that resolves to a non-empty string, so a
  // failed lookup never leaves a gap like "Client 2" with no "Client 1".
  clients
    .map((c) => formatClientLine(c))
    .filter(Boolean)
    .forEach((line, i) => push(`Client ${i + 1}`, line));

  push("File format", deal.fileFormat);
  push(
    "Commission",
    deal.totalcommissionrate ? `${deal.totalcommissionrate}%` : "",
  );
  push("In trust", yesNoLabel(deal.propertyTrust));
  push("Seller's disclosure", disclosureLabel(deal.sellerDisclosure));
  push("Pre-listing drop-off", yesNoLabel(deal.preListing));
  push("Drop-off by", deal.dropOff ? formatDate(deal.dropOff) : "");
  push("Photo book", yesNoLabel(deal.photoBook));
  push("Client referral", yesNoLabel(deal.clientReferal));
  if (deal.clientReferal === "yes") {
    push("Referred by", deal.referredSendGift);
  }
  push("Notes", deal.notes);

  return rows;
}
